// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import type React from 'react';
import { useCallback, useEffect, useId, useRef } from 'react';

import { Button, Card, CardBody, Image, Text } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { SlideUpQrCode } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { useShareFile } from '#src/app/pages/psf/promoteOrShare/shared/hooks';

const QrCodeSlot: React.FC<Pick<QrCodeViewProps, 'qrCode'>> = ({ qrCode }) => {
	const { translate } = useI18n();

	if (qrCode.status === 'ready') {
		return (
			<Image
				src={qrCode.imageBase64Uri}
				alt={translate('corgi.psf.promote_or_share.share_slide_up.qr_code.image_alt')}
				aspectRatio="1:1"
				className="size-full"
				data-testid="qr-code-view-image"
			/>
		);
	}
	if (qrCode.status === 'failed') {
		return (
			<div className="border-stroke-weak flex size-full items-center justify-center rounded-md border border-dashed p-4">
				<Text as="p" role="alert" size="small" fontWeight="bold" className="text-center">
					{translate('corgi.psf.promote_or_share.share_slide_up.qr_code.error')}
				</Text>
			</div>
		);
	}
	return (
		<div className="bg-fill-weaker size-full animate-pulse rounded-md" data-testid="qr-code-view-skeleton">
			<span className="sr-only">{translate('corgi.psf.promote_or_share.share_slide_up.qr_code.loading')}</span>
		</div>
	);
};

/**
 * EXPERIMENT: psf_promote_or_share_qr_code_experiment.
 * The QR view body. The slide-up owns the header (back and close).
 */
export const QrCodeView: React.FC<QrCodeViewProps> = ({ qrCode, petitionTitle, showPrint, onOpenPoster }) => {
	const { translate } = useI18n();
	const { shareFile } = useShareFile();
	const rootRef = useRef<HTMLElement>(null);
	const scanToSignId = useId();

	// The tile that opened this view unmounts, so focus moves here instead of to the page. The header has no
	// title, so the section's name tells screen readers the view changed.
	useEffect(() => {
		rootRef.current?.focus({ preventScroll: true });
	}, []);

	const handleSave = useCallback(() => {
		if (qrCode.status !== 'ready') return;
		shareFile(qrCode.file);
	}, [qrCode, shareFile]);

	return (
		<section
			ref={rootRef}
			tabIndex={-1}
			aria-labelledby={scanToSignId}
			className="flex flex-col items-center gap-4 outline-none"
			data-testid="qr-code-view"
		>
			<Card className="border-t-stroke-brand-strong w-full border-t-8">
				<CardBody className="flex flex-col items-center gap-2 text-center">
					<div className="aspect-square w-[70%]">
						<QrCodeSlot qrCode={qrCode} />
					</div>
					<Text as="p" id={scanToSignId} size="large" fontWeight="bold">
						{translate('corgi.psf.promote_or_share.share_slide_up.qr_code.scan_to_sign')}
					</Text>
					<Text as="p" fontWeight="bold" className="line-clamp-2" data-testid="qr-code-view-title">
						{petitionTitle}
					</Text>
				</CardBody>
			</Card>
			<Text as="p" size="small" className="text-typography-weak text-center">
				{translate('corgi.psf.promote_or_share.share_slide_up.qr_code.supporting_line')}
			</Text>
			<div className="flex w-full flex-col gap-2">
				<Button
					variant={showPrint ? 'primary' : 'secondary'}
					className="w-full"
					disabled={qrCode.status !== 'ready'}
					onClick={handleSave}
					data-testid="qr-code-view-save-button"
				>
					{translate('corgi.psf.promote_or_share.share_slide_up.qr_code.save_image')}
				</Button>
				{showPrint && (
					<Button
						variant="secondary"
						className="w-full"
						disabled={qrCode.status === 'failed'}
						onClick={onOpenPoster}
						data-testid="qr-code-view-print-button"
					>
						{translate('corgi.psf.promote_or_share.share_slide_up.qr_code.print_poster')}
					</Button>
				)}
			</div>
			<Text as="p" size="x-small" className="text-typography-weak text-center">
				{translate('corgi.psf.promote_or_share.share_slide_up.qr_code.brightness_hint')}
			</Text>
		</section>
	);
};

type QrCodeViewProps = {
	qrCode: SlideUpQrCode;
	petitionTitle: string;
	/** `variant_2` only. */
	showPrint: boolean;
	onOpenPoster: () => void;
};
